import React, { useState } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';

/**
 * Modal for confirming deletion of a security group.
 * Shows group details and handles the deletion process with proper confirmation.
 * - show {boolean}: Whether the modal is visible.
 * - onHide {() => void}: Callback to close the modal.
 * - group {Object}: The security group object to be deleted.
 * - onConfirm {(groupId: number) => Promise<void>}: Called when deletion is confirmed.
 * @returns {JSX.Element} A confirmation modal for security group deletion.
 */
const DeleteGroupModal = ({
  show,
  onHide,
  group,
  onConfirm
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handles the deletion confirmation process.
   * Shows a deleting state and calls the onConfirm callback.
   * @returns {Promise<void>}
   */
  const handleConfirmDelete = async () => {
    if (!group) return;

    setIsDeleting(true);
    setError('');

    try {
      await onConfirm(group.SecurityGroupID);
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to delete security group. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handles modal close and resets state.
   * @returns {void}
   */
  const handleClose = () => {
    setError('');
    setIsDeleting(false);
    onHide();
  };

  if (!group) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-exclamation-triangle text-warning mr-2"></i>
          Delete Security Group
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        <Alert variant="danger" className="mb-3">
          <strong>Warning:</strong> This action cannot be undone.
        </Alert>

        <p>Are you sure you want to delete the following security group?</p>

        <div className="bg-light p-3 rounded mb-3">
          <div className="row">
            <div className="col-sm-4">
              <strong>Group Name:</strong>
            </div>
            <div className="col-sm-8">
              {group.GroupName}
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <strong>Organization:</strong>
            </div>
            <div className="col-sm-8">
              {group.OrganizationName}
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <strong>Users:</strong>
            </div>
            <div className="col-sm-8">
              {group.UserCount || 0} users
            </div>
          </div>
          <div className="row">
            <div className="col-sm-4">
              <strong>Reports:</strong>
            </div>
            <div className="col-sm-8">
              {group.ReportCount || 0} reports
            </div>
          </div>
        </div>

        <div className="alert alert-info">
          <strong>What happens when you delete this group:</strong>
          <ul className="mb-0 mt-2">
            <li>All users will be removed from this group</li>
            <li>Users will lose access to reports that were granted through this group</li>
            <li>Individual user permissions will remain unchanged</li>
            <li>The group and all its associations will be permanently deleted</li>
          </ul>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleConfirmDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              Deleting...
            </>
          ) : (
            <>
              <i className="fas fa-trash mr-2"></i>
              Delete Group
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteGroupModal;
